import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { filenameFromHeaders, runAiJob } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader } from "../components/PageHeader";
import { ArrowSquareOut, DownloadSimple, UploadSimple, FilePdf, Clock, Sparkle } from "@phosphor-icons/react";

export default function ModulisticaNormativa() {
  const [links, setLinks] = useState(null);
  const [moduli, setModuli] = useState([]);
  const [loadingModuli, setLoadingModuli] = useState(false);

  useEffect(() => {
    api.get("/settings/normativa-toscana-links").then((r) => setLinks(r.data)).catch(() => setLinks({ sue_regione: [], normativa_nazionale: [], normativa_toscana: [] }));
    loadModuli();
  }, []);

  const loadModuli = () => {
    setLoadingModuli(true);
    api.get("/moduli-regionali").then((r) => setModuli(r.data || [])).catch(() => {}).finally(() => setLoadingModuli(false));
  };

  const sections = [
    { key: "sue_regione", title: "Portali ufficiali Regione Toscana", subtitle: "SUE, SIERT, STAR", accent: "border-l-blue-500", eyebrow: "Toscana" },
    { key: "normativa_nazionale", title: "Normativa nazionale", subtitle: "DPR 380, NTC 2018, DM 37 e altri riferimenti statali", accent: "border-l-slate-500", eyebrow: "Italia" },
    { key: "normativa_toscana", title: "Normativa Regione Toscana", subtitle: "Leggi regionali e regolamenti", accent: "border-l-amber-500", eyebrow: "Toscana" },
  ];

  return (
    <div className="p-4 md:p-8 w-full max-w-[1400px] mx-auto" data-testid="modulistica-normativa-page">
      <PageHeader
        eyebrow="Riferimenti ufficiali"
        title="Modulistica & Normativa"
        description="Modulistica edilizia Regione Toscana pronta all'uso, link ai portali ufficiali e testi normativi. Aggiorna i moduli manualmente caricando le nuove versioni."
      />

      {/* Moduli PDF Regione Toscana */}
      <Card className="border-slate-200 shadow-none overflow-hidden mb-8" data-testid="moduli-regionali-card">
        <div className="p-5 border-b border-slate-200 bg-blue-50/40">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700 mb-1">Regione Toscana</div>
              <h3 className="font-heading text-lg font-semibold">Moduli PDF ufficiali ({moduli.length})</h3>
              <p className="text-xs text-slate-500 mt-1">Modulistica edilizia unificata. Ogni modulo può essere compilato dalla Pratica o aggiornato caricando la nuova versione.</p>
            </div>
          </div>
        </div>
        {loadingModuli ? (
          <div className="p-8 text-center text-slate-400 text-sm font-mono">Caricamento moduli...</div>
        ) : moduli.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Nessun modulo disponibile.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {moduli.map((m) => (
              <ModuloRow key={m.codice} modulo={m} onUpdated={loadModuli} />
            ))}
          </ul>
        )}
      </Card>

      {/* Normativa + link */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sections.map((s) => {
          const items = links?.[s.key] || [];
          return (
            <Card key={s.key} className={`border-slate-200 shadow-none overflow-hidden border-l-4 ${s.accent}`} data-testid={`section-${s.key}`}>
              <div className="p-5 border-b border-slate-200">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{s.eyebrow}</div>
                <h3 className="font-heading text-base font-semibold text-slate-900">{s.title}</h3>
                <p className="text-xs text-slate-500 mt-1">{s.subtitle}</p>
              </div>
              <ul className="divide-y divide-slate-100">
                {items.length === 0 && !links && (<li className="p-4 text-xs text-slate-400 font-mono">Caricamento...</li>)}
                {items.length === 0 && links && (<li className="p-4 text-xs text-slate-400">Nessun link configurato.</li>)}
                {items.map((l) => (
                  <li key={l.url}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 px-4 py-3 hover:bg-slate-50 group" data-testid={`link-${s.key}`}>
                      <ArrowSquareOut size={14} weight="bold" className="mt-0.5 text-blue-600 flex-shrink-0" />
                      <span className="text-sm text-slate-800 group-hover:text-blue-700 leading-snug">{l.nome}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </Card>
          );
        })}
      </div>

      <p className="text-[11px] text-slate-400 font-mono mt-6">
        Fonte: portale ufficiale Regione Toscana. Le versioni caricate qui sono aggiornate manualmente dal titolare dello studio.
      </p>
    </div>
  );
}

function ModuloRow({ modulo, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [newDate, setNewDate] = useState(modulo.data_pubblicazione || "");
  const fileInputRef = useRef(null);
  const [guida, setGuida] = useState(null);
  const [loadingGuida, setLoadingGuida] = useState(false);

  const chiediGuida = async () => {
    setLoadingGuida(true);
    try {
      const data = await runAiJob("guida-modulo", { codice: modulo.codice });
      setGuida(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Errore IA");
    } finally { setLoadingGuida(false); }
  };

  const download = async () => {
    try {
      const resp = await api.get(`/moduli-regionali/${modulo.codice}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = filenameFromHeaders(resp.headers) || `${modulo.codice}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Errore download"); }
  };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const q = newDate.trim() ? `?data_pubblicazione=${encodeURIComponent(newDate.trim())}` : "";
      await api.post(`/moduli-regionali/${modulo.codice}/upload${q}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Modulo "${modulo.nome}" aggiornato`);
      setShowUpload(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); } catch { return ""; }
  };

  return (
    <>
      <li className="flex items-center gap-4 px-4 py-3 hover:bg-slate-50" data-testid={`modulo-row-${modulo.codice}`}>
        <div className="flex-shrink-0 w-10 h-10 rounded bg-red-50 border border-red-100 flex items-center justify-center">
          <FilePdf size={20} className="text-red-600" weight="regular" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 text-sm">{modulo.nome}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{modulo.codice}</span>
            {modulo.personalizzato && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">Aggiornato dallo studio</span>}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
            <span className="inline-flex items-center gap-1"><Clock size={11} /> Pubblicazione: <b className="text-slate-700">{modulo.data_pubblicazione || "—"}</b></span>
            <span>{modulo.pagine} pagine · {Math.round(modulo.size / 1024)} KB</span>
            {modulo.aggiornato_at && <span className="text-amber-700">ultimo agg. {fmtDate(modulo.aggiornato_at)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" variant="outline" onClick={chiediGuida} disabled={loadingGuida} data-testid={`btn-guida-${modulo.codice}`} className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900">
            <Sparkle size={13} weight="fill" className="text-amber-500" /> {loadingGuida ? "..." : "Come si compila"}
          </Button>
          <Button size="sm" variant="outline" onClick={download} data-testid={`btn-download-${modulo.codice}`}><DownloadSimple size={13} /> Scarica</Button>
          <Button size="sm" variant="outline" onClick={() => { setShowUpload(true); setNewDate(modulo.data_pubblicazione || ""); }} data-testid={`btn-upload-${modulo.codice}`}><UploadSimple size={13} /> Aggiorna</Button>
        </div>
      </li>

      <Dialog open={!!guida} onOpenChange={(o) => !o && setGuida(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="guida-modulo-dialog">
          <DialogHeader>
            <DialogTitle>Come si compila · {guida?.modulo?.nome}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="p-3 rounded border border-blue-200 bg-blue-50/60 text-slate-700">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Quando si usa</div>
              {guida?.quando_si_usa}
            </div>
            {guida?.sezioni?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Sezioni del modulo</div>
                <div className="space-y-2">
                  {guida.sezioni.map((s, i) => (
                    <div key={i} className="p-2.5 rounded border border-slate-200">
                      <div className="font-semibold text-slate-900">{s.sezione}</div>
                      <div className="text-slate-700 mt-0.5">{s.cosa_indicare}</div>
                      {s.attenzione && <div className="text-[11px] text-amber-800 mt-1">Attenzione: {s.attenzione}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {guida?.allegati?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">Allegati</div>
                <ul className="space-y-1">
                  {guida.allegati.map((a, i) => (
                    <li key={i} className="text-slate-700">
                      · {a.allegato} {a.obbligatorio ? <span className="text-red-700 font-semibold text-[11px]">obbligatorio</span> : <span className="text-slate-400 text-[11px]">se dovuto</span>}
                      {a.riferimento && <span className="font-mono text-[11px] text-slate-500"> {a.riferimento}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {guida?.attenzioni?.length > 0 && (
              <div className="p-3 rounded border border-amber-200 bg-amber-50/60">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Punti critici</div>
                <ul className="space-y-1 text-slate-700">
                  {guida.attenzioni.map((a, i) => <li key={i}>· {a}</li>)}
                </ul>
              </div>
            )}
            <div className="text-[10px] text-slate-400 leading-relaxed">{guida?.disclaimer}</div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Aggiorna modulo</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Modulo</div>
              <div className="text-sm font-medium">{modulo.nome}</div>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1 block">Data di pubblicazione della nuova versione</label>
              <Input value={newDate} onChange={(e) => setNewDate(e.target.value)} placeholder="Es. Gennaio 2026" data-testid="upload-modulo-date" />
              <p className="text-[10px] text-slate-500 mt-1">Trova la data in alto a sinistra sul modulo scaricato dal portale regionale.</p>
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1 block">File PDF</label>
              <input ref={fileInputRef} type="file" accept="application/pdf" className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-slate-900 file:text-white hover:file:bg-slate-800" data-testid="upload-modulo-file" />
              <p className="text-[10px] text-slate-500 mt-1">Max 20 MB · Solo PDF</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowUpload(false)}>Annulla</Button>
              <Button onClick={() => doUpload(fileInputRef.current?.files?.[0])} disabled={uploading} data-testid="upload-modulo-confirm">
                {uploading ? "Caricamento..." : "Carica nuova versione"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
